import Base from "@/controllers/base";
import { stringify } from "csv-stringify/browser/esm/sync";
import copy from "copy-text-to-clipboard";
import { isValid, parse } from "date-fns";

export default class DataGrid extends Base {
  constructor(elem) {
    super(elem);
  }

  static init() {
    document.querySelectorAll("table.data-grid").forEach((elem) => {
      new DataGrid(elem).init();
    });
  }

  init(options = {}) {
    super.init();
    let dataset = this.elem.dataset;
    let columns = this.initColumns();
    let initialSort = options.sorters || this.initSorts(columns);
    let renderVertical = dataset.virtual == "false" ? "basic" : "virtual";
    this.clearInvalidColumnDefinition(columns);
    this.table = new Tabulator(this.elem, {
      layout: dataset.layout || "fitColumns",
      height: dataset.height || false,
      minHeight: dataset.minHeight || false,
      maxHeight: dataset.maxHeight || false,
      renderVertical,
      columns,
      initialSort,
      sortOrderReverse: true,
      rowFormatter: this.rowFormatter.bind(this),
      frozenRows: this.frozenRows.bind(this),
      footerElement: this.footerElement(),
    });
    this.table.on("tableBuilt", () => {
      this.initControls();
      this.initCaption();
      this.initExportBtns();
      this.inputFilterInput();
      this.initListeners();
    });
    this.table.on("cellDblClick", function (e, cell) {
      if (e.target.tagName == "INPUT") {
        e.target.dispatchEvent(e);
      }
    });
  }

  initColumns() {
    const columns = [...this.elem.querySelectorAll("thead tr th")].map((th) => {
      var name = th.innerText.trim();
      var column = {
        title: th.textContent,
        field: th.textContent.replace(/\./g, "").toLowerCase(),
        formatter: "html",
        minWidth: "30px",
      };
      // can't have TH with the name ID
      if (/^id$/i.test(name)) {
        th.innerText = "#ID";
        column["title"] = "#ID";
        column["field"] = "#id";
      }

      if (th.dataset.frozen) {
        column["frozen"] = true;
      }

      if (th.dataset.formatter == "rich") {
        column["formatter"] = this.richFormatter.bind(this);
        column["sorter"] = this.richSorter.bind(this);
      }
      if (th.dataset.type == "datetime") {
        column["sorterParams"] = { format: th.dataset.format };
        column["sorter"] ||= this.datetimeSorter.bind(this);
      }
      if (th.dataset.type == "number") {
        column["headerHozAlign"] = "right";
        column["hozAlign"] = "right";
        column["sorter"] ||= this.numberSorter.bind(this);
        column["sorterParams"] = { type: "number" };
      }
      if (th.dataset.width) column["width"] = th.dataset.width || th.width;
      if (th.dataset.type) column["type"] = th.dataset.type;
      if (th.dataset.sort) column["sort"] = th.dataset.sort;
      if (th.dataset.sortable == "false") column["headerSort"] = false;
      if (th.dataset.type == "number") {
        column["headerHozAlign"] = "right";
        column["hozAlign"] = "right";
      }
      if (th.dataset.hoz) {
        column["headerHozAlign"] = th.dataset.hoz;
        column["hozAlign"] = th.dataset.hoz;
      }
      if (th.dataset.filter) {
        column["headerFilter"] = "input";
      }
      if (th.dataset.headerHidden == "true") {
        column["cssClass"] = "header-hidden";
        column["headerSort"] = false;
      }

      if (th.dataset.headerWordWrap == "true") column["headerWordWrap"] = true;
      if (this.elem.dataset.sortable == "false") column["headerSort"] = false;
      if (th.dataset.type == "checkbox") {
        column["title"] = th;
        column["field"] = "checkbox";
        column["formatter"] = this.richFormatter.bind(this);
        column["headerSort"] = false;
        column["width"] = 35;
      }
      return column;
    });
    return columns;
  }

  initSorts(columns) {
    return columns
      .filter((c) => c.sort)
      .map((c) => ({ column: c.field, dir: c.sort }));
  }

  // add class and style to rows & cells
  rowFormatter(row) {
    let tableRow = this.elem.querySelector(
      `tbody tr:nth-child(${row.getIndex() + 1})`
    );
    if (tableRow) {
      const cells = row.getElement().querySelectorAll(".tabulator-cell");
      tableRow.classList.forEach((c) => {
        row.getElement().classList.add(c);
      });
      this.moveStyles(tableRow, row.getElement());
      if (tableRow.dataset.key) row.key = tableRow.dataset.key;
      const rowColumns = tableRow.querySelectorAll("td");
      rowColumns.forEach((td, i) => {
        td.classList.forEach((c) => cells[i].classList.add(c));
        this.moveStyles(td, cells[i]);
      });
    }
  }

  moveStyles(from, to) {
    if (from.id) to.id = from.id;
    ["background"].forEach((name) => {
      if (from.style[name]) {
        to.style[name] = from.style[name];
      }
    });
  }

  frozenRows(row) {
    return row.getElement().classList.contains("frozen");
  }

  footerElement() {
    let elem = this.elem.nextElementSibling;
    return elem && elem.classList.contains("tabulator-footer") ? elem : null;
  }

  richFormatter(cell, formatterParams, onRendered) {
    const table = cell.getTable();
    const field = cell.getColumn().getField();
    let rowData = cell.getRow().getData();
    rowData.cachedDoms ||= {};
    if (!rowData.cachedDoms[field]) {
      let fields = table.columnManager.getColumns().map((c) => c.field);
      let cIndex = fields.findIndex((f) => f == field) + 1;
      let div = document.createElement("div");
      let rIndex = cell.getRow().getIndex() + 1;
      let selector = `tbody tr:nth-child(${rIndex}) td:nth-child(${cIndex})`;
      let column = this.elem.querySelector(selector);
      if (column) {
        while (column.firstChild) div.appendChild(column.firstChild);
        column.addEventListener(
          "revert-dom",
          () => {
            while (div.firstChild) column.appendChild(div.firstChild);
          },
          { once: true }
        );
      }
      rowData.cachedDoms[field] = div;
    }
    return rowData.cachedDoms[field];
  }

  //a, b - the two values being compared
  //aRow, bRow - the row components for the values being compared (useful if you need to access additional fields in the row data for the sort)
  //column - the column component for the column being sorted
  //dir - the direction of the sort ("asc" or "desc")
  //sorterParams - sorterParams object from column definition array
  richSorter(a, b, aRow, bRow, column, dir, sorterParams) {
    let dataLive =
      this.table.liveData &&
      new Date().getTime() - this.table.liveDateTime < 1500;
    if (!dataLive) {
      this.table.liveData = this.liveData();
      this.table.liveDateTime = new Date().getTime();
    }
    let { title } = column.getDefinition();
    let cIndex = this.table.liveData[0].indexOf(title);
    let aData = this.table.liveData.find((row) => row[0] == aRow.getIndex());
    let bData = this.table.liveData.find((row) => row[0] == bRow.getIndex());
    if (sorterParams.type == "number") {
      return this.numberSorter(
        aData[cIndex],
        bData[cIndex],
        aRow,
        bRow,
        column,
        dir,
        sorterParams
      );
    } else {
      return (aData[cIndex] || "").localeCompare(bData[cIndex] || "");
    }
  }

  datetimeSorter(a, b, aRow, bRow, column, dir, sorterParams) {
    let f = column.getField();
    let s = "[data-sort]";
    let asort = aRow.getCell(f).getElement().querySelector(s)?.dataset?.sort;
    let bsort = bRow.getCell(f).getElement().querySelector(s)?.dataset?.sort;
    asort = parse(asort || a, sorterParams.format, new Date());
    bsort = parse(bsort || b, sorterParams.format, new Date());
    asort = isValid(asort) ? asort : new Date(1900, 1, 1);
    bsort = isValid(bsort) ? bsort : new Date(1900, 1, 1);
    return asort - bsort;
  }

  numberSorter(a, b, aRow, bRow, column, dir, sorterParams) {
    let f = column.getField();
    let s = "[data-sort]";
    let asort = aRow.getCell(f).getElement().querySelector(s)?.dataset?.sort;
    let bsort = bRow.getCell(f).getElement().querySelector(s)?.dataset?.sort;
    asort = parseFloat(asort ? asort : a.replace(/[^0-9.-]+/g, "") || "0");
    bsort = parseFloat(bsort ? bsort : b.replace(/[^0-9.-]+/g, "") || "0");
    return asort - bsort;
  }

  customCsvFormatter(list, options, setFileContents) {
    //trigger file download, passing the formatted data and mime type
    setFileContents(stringify(this.liveData()), "text/csv");
  }

  customFilter(data, filterParams) {
    let value = filterParams.value.trim();
    if (value.isBlank()) return true;
    let texts = this.table.liveData.find((row) => row[0] == data.id) || [];
    return texts.join(" ").toLowerCase().includes(value.toLowerCase());
  }

  initControls() {
    let tDiv = this.table.element;
    let tDivParent = tDiv.parentNode;
    if (tDivParent.querySelector(".tabulator-controls")) return;
    let div = document.createElement("div");
    let left = document.createElement("div");
    let center = document.createElement("div");
    let right = document.createElement("div");
    div.classList.add("tabulator-controls");
    left.classList.add("left");
    center.classList.add("center");
    right.classList.add("right");
    div.appendChild(left);
    div.appendChild(center);
    div.appendChild(right);
    this.captionElem = tDivParent.insertBefore(div, tDiv);
  }

  inputFilterInput() {
    if (this.elem.dataset.filter == "true") {
      const input = document.createElement("input");
      input.placeholder = "Enter Keyword";
      input.style.width = "150px";
      input.observeField(500, () => {
        this.table.liveData = this.liveData();
        this.table.setFilter(this.customFilter.bind(this), {
          value: input.value,
        });
      });
      this.captionElem.querySelector(".right").appendChild(input);
    }
  }

  initExportBtns() {
    const exports = (this.elem.dataset.export || "").split(",");
    if (exports.includes("CSV")) {
      var btn = document.createElement("a");
      btn.textContent = "Export to CSV";
      btn.classList.add("btn", "btn-default");
      btn.addEventListener("click", () => {
        this.table.download(this.customCsvFormatter.bind(this), "data.csv");
      });
      this.captionElem.querySelector(".right").appendChild(btn);
    }
    if (exports.includes("COPY")) {
      var btn = document.createElement("a");
      btn.textContent = "Copy Data";
      btn.classList.add("btn", "btn-default");
      btn.addEventListener("click", () => {
        copy(stringify(this.liveData(), { delimiter: "  " }));
        NApp.flashNoticeOut(
          "The table data has been copied to your clipboard."
        );
      });
      this.captionElem.querySelector(".right").appendChild(btn);
    }
  }

  initCaption() {
    let caption = this.elem.querySelector("caption");
    if (caption)
      this.captionElem.querySelector(".left").appendChild(caption.firstChild);
  }

  initListeners() {
    let tDiv = this.table.element;
    tDiv.addEventListener("tabulator", (e) => {
      e.target.table = this.table;
    });
    tDiv.addEventListener("del-row", (e) => {
      let rows = this.table.getRows();
      let row = rows.find((row) => row.key == e.detail.key);
      if (row) this.table.deleteRow(row.getData().id);
    });
    tDiv.addEventListener("add-row", (e) => {
      let sorters = this.table.getSorters();
      sorters.forEach((s) => (s.column = s.field));
      let event = new CustomEvent("revert-dom");
      this.elem.querySelectorAll("td").forEach((td) => td.dispatchEvent(event));
      let tbody = document.createElement("tbody");
      tbody.innerHTML = e.detail.html;
      this.elem.querySelector("tbody").appendChild(tbody.querySelector("tr"));
      this.table.destroy();
      tDiv.parentNode.replaceChild(this.elem, this.table.element);
      NApp.dispatchTurboLoad();
      this.init({ sorters });
    });
  }

  clearInvalidColumnDefinition(columns) {
    columns.forEach((column) => {
      delete column["sort"];
      delete column["type"];
    });
  }
  liveData() {
    const rows = [];
    rows.push(["#", ...this.table.getColumnDefinitions().map((c) => c.title)]);
    const visibleSelector = ":not(input[type=hidden]):not(br):not(hr)";
    this.table
      .getRows()
      .sort((a, b) => a.getPosition() - b.getPosition())
      .forEach((row) => {
        const items = [row.getIndex()];
        const columns = row.getCells().map((c) => c.getElement());
        columns.forEach((column) => {
          var node = column.cloneNode(true);
          document.body.appendChild(node);
          var visibleElems = node.querySelectorAll(visibleSelector);
          for (const velem of visibleElems) {
            if (velem.isVisible()) {
              if (/INPUT|TEXTAREA/.test(velem.tagName)) {
                var div = document.createElement("div");
                if (/text|textarea/.test(velem.type)) {
                  div.innerText = velem.value;
                }
                if (velem.type === "checkbox") {
                  div.innerText = velem.checked ? "Yes" : "No";
                }
                velem.parentNode.appendChild(div);
                velem.parentNode.removeChild(velem);
              }
              if (velem.tagName === "SELECT") {
                var div = document.createElement("div");
                div.innerText = velem.selectedOptions[0].innerText;
                velem.parentNode.appendChild(div);
                velem.parentNode.removeChild(velem);
              }
            } else {
              velem.parentNode.removeChild(velem);
            }
          }
          items.push(node.innerText.trim());
          document.body.removeChild(node);
        });
        rows.push(items);
      });
    return rows;
  }

  static delRow(id, key) {
    let event = new CustomEvent("del-row", { detail: { key } });
    let target = document.querySelector(`.tabulator#${id}`);
    if (target) target.dispatchEvent(event);
  }

  static addRow(id, html) {
    let event = new CustomEvent("add-row", { detail: { html } });
    let target = document.querySelector(`.tabulator#${id}`);
    if (target) target.dispatchEvent(event);
  }
}
document.addEventListener("turbo:load", () => {
  // use 250: higher than NApp
  setTimeout(() => DataGrid.init(), 250);
});
